import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../utils/api';
import PublicChatWidget from '../components/PublicChatWidget';

const SUBJECTS = ['Försäljning', 'Support', 'Partnerskap', 'Annat'];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', subject: 'Försäljning', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [supportHours, setSupportHours] = useState(null);

  useEffect(() => {
    api.getSupportHours().then((r) => setSupportHours(r.data)).catch(() => {});
  }, []);

  const updateField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Fyll i namn, e-post och meddelande');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitContactForm(form);
      setDone(true);
      toast.success('Tack! Vi har tagit emot ditt meddelande.');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Kunde inte skicka meddelandet');
    } finally {
      setSubmitting(false);
    }
  };

  const isOpen = !!supportHours?.is_open;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }} data-testid="contact-page">
      {/* Header — match landing */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#F7F4EF]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/operio-logo-2.png" alt="Operio" className="h-7 sm:h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#6B6560]">
            <Link to="/#funktioner" className="hover:text-[#1A1A1A] transition-colors">Funktioner</Link>
            <Link to="/#priser" className="hover:text-[#1A1A1A] transition-colors">Priser</Link>
            <Link to="/about" className="hover:text-[#1A1A1A] transition-colors">Om oss</Link>
            <Link to="/kontakt" className="text-[#1A1A1A] font-medium">Kontakt</Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link to="/login" className="text-xs sm:text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] transition-colors px-2">
              Logga in
            </Link>
            <Link to="/register">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-5 rounded-full whitespace-nowrap">
                Kom igång<span className="hidden sm:inline"> gratis</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 sm:pt-28 pb-20">
        {/* Hero */}
        <section className="px-5 sm:px-6">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Vi finns här för dig</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.05]"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Säg hej.
            </h1>
            <p className="text-base text-[#6B6560] mt-5 max-w-xl mx-auto leading-relaxed">
              Vill du veta mer om Operio, ha en demo eller är fastlåst i en funktion?
              Skicka ett meddelande så hör vi av oss — oftast samma dag.
            </p>
          </div>
        </section>

        {/* Grid: info + form */}
        <section className="px-5 sm:px-6 mt-12 sm:mt-16">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
            {/* Left: info cards */}
            <aside className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid="contact-email-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#E8613A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#E8613A]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#7A7570] uppercase tracking-wider">E-post</p>
                    <a href="mailto:kontakt@operio.nu"
                       className="text-base font-bold text-[#1A1A1A] hover:text-[#E8613A] transition-colors block mt-1 break-all"
                       data-testid="contact-email-link">
                      kontakt@operio.nu
                    </a>
                    <p className="text-xs text-[#6B6560] mt-1.5">Svar vanligtvis inom 1 arbetsdag.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid="contact-hours-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#E8613A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-[#E8613A]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#7A7570] uppercase tracking-wider">Kundtjänst</p>
                    <p className="text-base font-bold text-[#1A1A1A] mt-1">
                      {supportHours?.hours_label?.replace(' (svensk tid)', '') || 'Vardagar 09:00 – 17:00'}
                    </p>
                    <p className={`text-xs mt-1.5 inline-flex items-center gap-1.5 ${isOpen ? 'text-green-700' : 'text-amber-700'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500' : 'bg-amber-500'}`} />
                      {isOpen ? 'Öppet nu' : 'Stängt just nu — AI-chatten svarar 24/7'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid="contact-location-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#E8613A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#E8613A]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#7A7570] uppercase tracking-wider">Plats</p>
                    <p className="text-base font-bold text-[#1A1A1A] mt-1">Skövde, Sverige</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A1A1A] rounded-2xl p-5 text-white relative overflow-hidden">
                <img src="/operio-mascot-3d.png" alt="" className="absolute -right-4 -bottom-4 w-28 h-28 opacity-90 pointer-events-none" />
                <p className="text-xs font-medium text-white/60 uppercase tracking-wider">Snabbast</p>
                <p className="text-sm font-medium mt-2 max-w-[220px]">
                  Klicka på maskoten nere till höger för att chatta direkt — Operio AI svarar dygnet runt.
                </p>
              </div>
            </aside>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 sm:p-8 shadow-sm">
                {done ? (
                  <div className="text-center py-10" data-testid="contact-form-success">
                    <div className="w-14 h-14 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 size={28} className="text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#1A1A1A] mt-5"
                        style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      Tack {form.name.split(' ')[0]}!
                    </h2>
                    <p className="text-sm text-[#6B6560] mt-2 max-w-sm mx-auto">
                      Vi har tagit emot ditt meddelande och hör av oss till <strong>{form.email}</strong> så snart vi kan.
                    </p>
                    <button
                      onClick={() => { setDone(false); setForm({ name:'', email:'', phone:'', company:'', subject:'Försäljning', message:'' }); }}
                      className="text-sm text-[#E8613A] hover:underline mt-6 font-medium"
                      data-testid="contact-form-send-another"
                    >
                      Skicka ytterligare ett meddelande
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">Namn *</Label>
                        <Input value={form.name} onChange={updateField('name')} placeholder="Förnamn Efternamn"
                               className="mt-1.5 h-11" required data-testid="contact-name" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">E-post *</Label>
                        <Input type="email" value={form.email} onChange={updateField('email')}
                               placeholder="din@epost.se" className="mt-1.5 h-11" required data-testid="contact-email-input" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">Telefon</Label>
                        <Input value={form.phone} onChange={updateField('phone')} placeholder="070-123 45 67"
                               className="mt-1.5 h-11" data-testid="contact-phone" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">Företag</Label>
                        <Input value={form.company} onChange={updateField('company')} placeholder="Företagets namn"
                               className="mt-1.5 h-11" data-testid="contact-company" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">Ärendetyp</Label>
                      <div className="flex flex-wrap gap-2 mt-2" data-testid="contact-subject-group">
                        {SUBJECTS.map((s) => (
                          <button
                            type="button"
                            key={s}
                            onClick={() => setForm({ ...form, subject: s })}
                            className={`text-sm px-4 h-9 rounded-full border transition-all ${
                              form.subject === s
                                ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                                : 'bg-white text-[#6B6560] border-[#E8E2D9] hover:border-[#1A1A1A]'
                            }`}
                            data-testid={`contact-subject-${s.toLowerCase()}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-[#7A7570]">Meddelande *</Label>
                      <textarea
                        value={form.message}
                        onChange={updateField('message')}
                        rows={6}
                        required
                        placeholder="Berätta kort vad vi kan hjälpa dig med..."
                        className="mt-1.5 w-full text-sm rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E8613A]/30 focus:border-[#E8613A]"
                        data-testid="contact-message"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                      <p className="text-xs text-[#7A7570]">
                        Vi använder dina uppgifter enbart för att besvara ditt meddelande.
                      </p>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-11 px-6 rounded-full font-semibold"
                        data-testid="contact-submit-btn"
                      >
                        {submitting ? 'Skickar…' : (<>Skicka <Send size={14} className="ml-2" /></>)}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mt-20 px-5 sm:px-6">
          <div className="max-w-4xl mx-auto bg-[#1A1A1A] rounded-2xl px-6 py-10 sm:px-10 sm:py-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Hellre testa direkt?
            </h2>
            <p className="text-white/70 mt-3 max-w-md mx-auto text-sm">
              Skapa konto gratis i 14 dagar — inga kortuppgifter krävs förrän du bestämmer dig.
            </p>
            <Link to="/register" className="inline-block mt-6">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">
                Starta gratis <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-5 sm:px-6 border-t border-[#E8E2D9]" style={{ backgroundColor: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/operio-logo-2.png" alt="Operio" className="h-6 w-auto" />
          </Link>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-[#7A7570]">
            <Link to="/#funktioner" className="hover:text-[#1A1A1A]">Funktioner</Link>
            <Link to="/#priser" className="hover:text-[#1A1A1A]">Priser</Link>
            <Link to="/about" className="hover:text-[#1A1A1A]">Om oss</Link>
            <Link to="/kontakt" className="hover:text-[#1A1A1A]">Kontakt</Link>
            <Link to="/login" className="hover:text-[#1A1A1A]">Logga in</Link>
          </nav>
          <p className="text-xs text-[#7A7570]">&copy; 2026 Operio AB</p>
        </div>
      </footer>

      <PublicChatWidget />
    </div>
  );
};

export default ContactPage;
